import { computed, Ref, unref } from 'vue'
import { errorSoundList, warningSoundList, getSoundObject } from '@/composables/globalData/useEntitiesData'
import { WidgetExtended } from '@/types/dashboard'
import { timeFormatter } from '@/helpers/timeFormatter'
import { ISoundItem } from '@/types/sound'
import i18n from '@/i18n'
import { setupSoundQueue } from '@/composables/useSoundsStoreData'


export const limitThresholdValue = 'limit'
export const warningThresholdValue = 'warning'

export interface NotificationSetupParams {
    entityName: string
    property: string
    thresholdValue?: number | string
    currentValue: number | string
    type: string
    id: string
    soundPlayed: boolean
    timerStartValue?: number
    notificationTag: string

}

export function useSoundsThresholdNotification (
    widgetOrRef: WidgetExtended | Ref<WidgetExtended>
) {

    const limitFromSettings = computed(() => {
        const widget = unref(widgetOrRef)
        return widget.WidgetLayout?.LimitMaxCounter || 0
    })

    const warningFromSettings = computed(() => {
        const widget = unref(widgetOrRef)
        return widget.WidgetLayout?.WarningMaxCounter || 0
    })

    const reverseThreshold = computed(() => {
        const widget = unref(widgetOrRef)
        return widget.WidgetLayout?.reverseThreshold ?? false
    })

    const warningSound = computed(() => {
        const widget = unref(widgetOrRef)
        if (widget.WidgetLayout?.soundWarning) {
            return getSoundObject(warningSoundList.value, widget.WidgetLayout?.soundWarning)?.Rec_Path || ''
        }
        return ''
    })

    const errorSound = computed(() => {
        const widget = unref(widgetOrRef)
        if (widget.WidgetLayout?.soundLimit) {
            return getSoundObject(errorSoundList.value, widget.WidgetLayout.soundLimit)?.Rec_Path || ''
        }
        return ''
    })

    const getThresholdValue = (type: string) => {
        if (type === warningThresholdValue) {
            return warningFromSettings.value
        }
        return limitFromSettings.value

    }
    const getCurrentNotificationStatus = (val: number, reverseThreshold = false) => {
        let currentNotificationStatus = ''

        if (reverseThreshold) {
            if (warningFromSettings.value && warningFromSettings.value > 0 && val <= warningFromSettings.value) {
                currentNotificationStatus = warningThresholdValue
            }
            if (limitFromSettings.value && limitFromSettings.value > 0 && val <= limitFromSettings.value) {
                currentNotificationStatus = limitThresholdValue
            }
        } else {
            if (warningFromSettings.value && warningFromSettings.value > 0 && val >= warningFromSettings.value) {
                currentNotificationStatus = warningThresholdValue
            }
            if (limitFromSettings.value && limitFromSettings.value > 0 && val >= limitFromSettings.value) {
                currentNotificationStatus = limitThresholdValue
            }
        }

        return currentNotificationStatus
    }

    const setupNotification = ({
        entityName,
        property,
        currentValue,
        type,
        id,
        soundPlayed,
        timerStartValue,
        notificationTag,
    }: NotificationSetupParams) => {
        const notificationData = {
            notification_value: timerStartValue ?  timeFormatter(getThresholdValue(type)): getThresholdValue(''),
            entity_name: entityName,
            current_value: currentValue,
            property: property,
            type: type
        }
        const soundItem: ISoundItem = {
            id: id,
            sound: type === limitThresholdValue ? errorSound.value : warningSound.value,
            played: soundPlayed,
            isPlaying: false,
            timestamp: new Date().getTime(),
            notificationHTML: i18n.global.t(`${notificationTag}`, { ...notificationData })

        }

        if (timerStartValue) {
            soundItem.timerStartValue = timerStartValue
        }

        if (soundItem.sound) {
            setupSoundQueue(soundItem)
        }

    }

    return {
        limitFromSettings,
        warningFromSettings,
        warningSound,
        errorSound,
        reverseThreshold,
        setupNotification,
        getThresholdValue,
        getCurrentNotificationStatus
    }
}




